import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { colors, fonts } from '../brand';
import { useAuth } from '../context/AuthContext';

export default function SiteNav() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const navLinks = [
    { label: 'Home', path: '/' },
    { label: 'Listen', path: '/listen' },
    { label: 'Q&A', path: '/qa' },
    { label: 'Refuge', path: '/refuge' },
    { label: 'Resources', path: '/resources' },
    { label: 'Roadmap', path: '/quiz' },
    { label: 'About', path: '/about' },
  ];

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <header style={styles.header}>
      <Link to="/" style={styles.logoArea}>
        <img src="/logo.svg" alt="Elephants Answering" style={styles.logoIcon} />
        <div>
          <div style={styles.logoText}>Elephants Answering, PBC</div>
          <div style={styles.logoSub}>Divorce Support &amp; Refuge</div>
        </div>
      </Link>

      {isMobile ? (
        <>
          <button style={styles.hamburger} onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
            <span style={styles.bar} />
            <span style={styles.bar} />
            <span style={styles.bar} />
          </button>
          {menuOpen && (
            <nav style={styles.mobileMenu} aria-label="Main navigation">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  style={{
                    ...styles.mobileNavLink,
                    color: isActive(link.path) ? colors.rose : colors.navy,
                    fontWeight: isActive(link.path) ? '600' : 'normal',
                  }}
                >
                  {link.label}
                </Link>
              ))}
              <div style={styles.mobileDivider} />
              <a href="https://buy.stripe.com/7sYbJ13FE1Ny2yQ6w9cMM00" target="_blank" rel="noopener noreferrer" style={styles.mobileSupportBtn}>Support Us</a>
              <Link to="/contact" style={styles.mobileNavLink}>Contact</Link>
              {user ? (
                <>
                  <Link to="/dashboard" style={styles.mobileNavLink}>My Account</Link>
                  <button style={styles.mobileNavBtn} onClick={() => { logout(); setMenuOpen(false); }}>Sign Out</button>
                </>
              ) : (
                <>
                  <Link to="/login" style={styles.mobileNavLink}>Sign In</Link>
                  <Link to="/register" style={{ ...styles.mobileNavLink, color: colors.rose, fontWeight: '600' }}>Join Today</Link>
                </>
              )}
            </nav>
          )}
        </>
      ) : (
        <nav style={styles.headerLinks} aria-label="Main navigation">
          {navLinks.map(link => (
            <Link
              key={link.path}
              to={link.path}
              style={{
                ...styles.navLink,
                color: isActive(link.path) ? colors.rose : colors.navy,
                fontWeight: isActive(link.path) ? '600' : 'normal',
              }}
            >
              {link.label}
            </Link>
          ))}
          <a href="https://buy.stripe.com/7sYbJ13FE1Ny2yQ6w9cMM00" target="_blank" rel="noopener noreferrer" style={styles.supportBtn}>Support Us</a>
          {user ? (
            <>
              <Link to="/dashboard" style={styles.loginLink}>My Account</Link>
              <button style={styles.loginBtn} onClick={logout}>Sign Out</button>
            </>
          ) : (
            <>
              <Link to="/login" style={styles.loginLink}>Sign In</Link>
              <Link to="/register" style={styles.signupLink}>Join Today</Link>
            </>
          )}
        </nav>
      )}
    </header>
  );
}

const styles = {
  header: { backgroundColor: colors.white, padding: '16px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: `0 1px 0 ${colors.peach}`, position: 'sticky', top: 0, zIndex: 100 },
  logoArea: { display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none' },
  logoIcon: { height: '88px', width: 'auto' },
  logoText: { fontFamily: fonts.heading, color: colors.navy, fontSize: '17px', fontWeight: '600', letterSpacing: '0.5px' },
  logoSub: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '11px', letterSpacing: '0.3px', marginTop: '3px' },

  headerLinks: { display: 'flex', gap: '4px', alignItems: 'center' },
  navLink: { textDecoration: 'none', color: colors.navy, padding: '8px 12px', fontSize: '13px', fontFamily: fonts.body, letterSpacing: '0.3px' },
  loginLink: { textDecoration: 'none', border: `1px solid ${colors.peach}`, color: colors.navy, padding: '8px 20px', borderRadius: '2px', fontSize: '13px', fontFamily: fonts.body, marginLeft: '8px', display: 'inline-block' },
  loginBtn: { backgroundColor: 'transparent', border: `1px solid ${colors.peach}`, color: colors.navy, padding: '8px 20px', borderRadius: '2px', cursor: 'pointer', fontSize: '13px', fontFamily: fonts.body, marginLeft: '8px' },
  signupLink: { backgroundColor: colors.rose, textDecoration: 'none', color: colors.white, padding: '8px 20px', borderRadius: '2px', fontSize: '13px', fontFamily: fonts.body, fontWeight: '600', display: 'inline-block' },
  supportBtn: { backgroundColor: colors.gold, color: colors.navy, padding: '8px 20px', borderRadius: '2px', fontSize: '13px', fontFamily: fonts.body, fontWeight: '600', textDecoration: 'none', marginLeft: '8px', display: 'inline-block' },

  hamburger: { display: 'flex', flexDirection: 'column', gap: '5px', background: 'none', border: 'none', cursor: 'pointer', padding: '8px' },
  bar: { display: 'block', width: '24px', height: '2px', backgroundColor: colors.navy, borderRadius: '2px' },

  mobileMenu: {
    position: 'absolute', top: '100%', left: 0, right: 0,
    backgroundColor: colors.white,
    borderTop: `1px solid ${colors.peach}`,
    borderBottom: `2px solid ${colors.peach}`,
    display: 'flex', flexDirection: 'column',
    padding: '8px 0', zIndex: 200,
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  },
  mobileNavLink: { textDecoration: 'none', color: colors.navy, padding: '14px 32px', fontSize: '15px', fontFamily: fonts.body, letterSpacing: '0.3px', display: 'block' },
  mobileNavBtn: { backgroundColor: 'transparent', border: 'none', color: colors.navy, padding: '14px 32px', cursor: 'pointer', fontSize: '15px', fontFamily: fonts.body, textAlign: 'left', letterSpacing: '0.3px' },
  mobileSupportBtn: { display: 'block', color: colors.gold, padding: '14px 32px', fontSize: '15px', fontFamily: fonts.body, fontWeight: '600', letterSpacing: '0.3px', textDecoration: 'none' },
  mobileDivider: { borderTop: `1px solid ${colors.peach}`, margin: '8px 0' },
};
